import React, { useState } from 'react';
import api from '../../services/api';

const FileUpload = ({ requirementId, sectionId, onUpload, existingDocuments = [], onDelete }) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'image/png', 'image/jpeg', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      setError('Invalid file type. Only PDF, DOC, DOCX, PNG, and JPG files are allowed.');
      return;
    }

    // Validate file size (10MB)
    if (file.size > 10 * 1024 * 1024) {
      setError('File size must be less than 10MB.');
      return;
    }

    setError('');
    setUploading(true);

    const formData = new FormData();
    formData.append('file', file);
    formData.append('requirementId', requirementId);
    formData.append('sectionId', sectionId);

    try {
      const response = await api.post('/documents/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.success) {
        onUpload(response.data.data.document);
      }
    } catch (error) {
      setError(error.response?.data?.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (docId) => {
    try {
      await api.delete(`/documents/${docId}`);
      onDelete(docId);
    } catch (error) {
      setError(error.response?.data?.message || 'Delete failed');
    }
  };

  return (
    <div className="space-y-4">
      <div className="border-2 border-dashed border-border rounded-lg p-6 text-center hover:border-primary transition-colors">
        <input
          type="file"
          id={`file-${requirementId}`}
          className="hidden"
          onChange={handleFileSelect}
          accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
          disabled={uploading}
        />
        <label
          htmlFor={`file-${requirementId}`}
          className="cursor-pointer"
        >
          <div className="text-grey">
            <svg className="mx-auto h-12 w-12 text-grey" stroke="currentColor" fill="none" viewBox="0 0 48 48">
              <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <p className="mt-2 text-sm">
              {uploading ? 'Uploading...' : 'Click to upload or drag and drop'}
            </p>
            <p className="text-xs text-lightGrey mt-1">
              PDF, DOC, DOCX, PNG, JPG up to 10MB
            </p>
          </div>
        </label>
      </div>

      {error && (
        <div className="text-red-500 text-sm">{error}</div>
      )}

      {existingDocuments.length > 0 && (
        <div className="space-y-2">
          <p className="text-sm font-medium text-grey">Uploaded documents:</p>
          {existingDocuments.map((doc) => (
            <div key={doc.id} className="flex items-center justify-between bg-white p-3 rounded border border-border">
              <div className="flex items-center space-x-3">
                <svg className="h-5 w-5 text-grey" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span className="text-sm text-grey truncate max-w-xs">{doc.original_name}</span>
              </div>
              <div className="flex items-center space-x-2">
                <a
                  href={`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/documents/${doc.id}/download`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:text-green-700 text-sm"
                >
                  Download
                </a>
                <button
                  onClick={() => handleDelete(doc.id)}
                  className="text-red-500 hover:text-red-700 text-sm"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default FileUpload;
